// wireEvents — observes consecutive game snapshots and emits the
// gameplay-event lines WinBolo writes to its news wire. We mirror the
// canonical six strings from references/winbolo/winbolo/src/gui/
// linresource.h:312-318 verbatim:
//
//   "<Name> captured a Neutral Base"
//   "<Name> just stole base from <OldName>"
//   "<Name> captured a Neutral Pillbox"
//   "<Name> just stole pillbox from <OldName>"
//   "<Name> just lost his builder"
//   "\"<Name>\" has quit game"
//
// And four intentional divergences from WinBolo for modern multiplayer
// legibility:
//
//   "<A> and <B> just formed an alliance"
//   "<A> and <B> just broke their alliance"
//   "<Name> just drowned in deep sea"
//   "\"<Name>\" has joined the game"
//
// Alliance divergence rationale: WinBolo's alliance flow is a popup
// dialog ("X requests alliance. Accept?") with private networkStatus
// follow-ups, only visible to the two parties. That doesn't translate
// to Bolo2K — we use chat-command + panel-button alliance UX (see
// `project_alliance_panel_removal.md`), so other players have no way to
// know an alliance happened unless we surface it. A per-pair cooldown
// caps form/break spam.
//
// Drowned divergence rationale: WinBolo has the string already
// (MESSAGE_TANKSUNK), but emits it via the `assistantMessage` channel
// — self-only, drowning player sees it. Promoting to the public wire
// surfaces a rare, flavourful death type that's obvious to other
// players watching the field anyway (the sinking sound, the tank
// vanishing into water).
//
// Joined divergence rationale: pure asymmetry fix. We already fire
// "has quit game" when a player slot empties; surfacing the inverse
// keeps the wire's coverage of the player roster symmetric.
//
// Kill feeds and other combat events stay off the wire — WinBolo
// doesn't broadcast those and the rationale (kill spam, surveillance,
// "combat is loud enough on its own") still holds.
//
// The server doesn't broadcast any of this as text — clients infer it
// from snapshot deltas. This mirrors how WinBolo and Cheshire's original
// build it: every client reconstructs the wire locally from observed
// world state.
//
// First snapshot after construction (or after reset()) is treated as a
// baseline — we record state but emit nothing, so the initial welcome
// burst doesn't replay every existing ownership as a "capture".

const NEUTRAL = 0xff;
const LGM_PARACHUTING = 5; // LGMStatus.Parachuting — entered only after death.
// Per-pair cooldown for alliance form/break events on the wire. Caps the
// damage from rapid form-break-form spam: any subsequent transition for
// the same pair within this window updates the cooldown clock but stays
// off the wire. 10 s is long enough to swallow accidental misclicks and
// hostile spamming, short enough that genuine separated diplomacy fires.
const ALLIANCE_COOLDOWN_MS = 10_000;

export interface WireSink {
  add(text: string): void;
}

interface BaseLite {
  id: number;
  owner: number;
}
interface PillLite {
  id: number;
  owner: number;
  carriedBy: number;
}
interface TankLite {
  id: number;
  owner: number;
  alive: boolean;
  x: number;
  y: number;
  lgmStatus: number;
}

export interface SnapshotForDiff {
  bases: ReadonlyArray<{ id: number; owner: number }>;
  pillboxes: ReadonlyArray<{ id: number; owner: number; carriedBy: number }>;
  tanks: ReadonlyArray<{ id: number; owner: number; alive: boolean; x: number; y: number; lgmStatus: number }>;
  playerNames: ReadonlyArray<string>;
  // Per-slot bitmaps of mutual-pact allies (snapshot.allies). Used to
  // suppress allied-migration noise on the wire.
  allies: ReadonlyArray<number>;
  // Transient sound triggers from this tick — used to detect drowning
  // (a `tankSinking` event at the death tile pinpoints which dying tank
  // sunk vs. which one exploded normally).
  soundEvents: ReadonlyArray<{ kind: string; x: number; y: number }>;
}

interface Captured {
  bases: Map<number, BaseLite>;
  pillboxes: Map<number, PillLite>;
  tanks: Map<number, TankLite>;
  playerNames: string[];
  allies: number[];
  sunkAt: Array<{ x: number; y: number }>;
}

export class WireEvents {
  private prev: Captured | null = null;
  // Per-pair (canonical "minSlot:maxSlot") last-fired timestamp for the
  // alliance-event cooldown. We only fire if the elapsed time since the
  // last event for this pair exceeds ALLIANCE_COOLDOWN_MS — suppressed
  // transitions still update the timestamp so a sustained spam loop stays
  // off the wire indefinitely.
  private alliancePairLastFired = new Map<string, number>();
  // Injectable clock so tests can drive cooldown windows deterministically.
  private now: () => number;

  constructor(private readonly sink: WireSink, now?: () => number) {
    this.now = now ?? (() => Date.now());
  }

  /** Re-seed: drop the prev snapshot so the next observe() doesn't emit
   *  anything. Call this on (re)connect / welcome so the first snapshot
   *  after a fresh session is treated as the baseline rather than a diff. */
  reset(): void {
    this.prev = null;
    this.alliancePairLastFired.clear();
  }

  observe(snap: SnapshotForDiff): void {
    const next = capture(snap);
    const prev = this.prev;
    this.prev = next;
    if (!prev) return;

    this.detectBaseChanges(prev, next);
    this.detectPillChanges(prev, next);
    this.detectLgmDeaths(prev, next);
    this.detectAllianceChanges(prev, next);
    this.detectDrownings(prev, next);
    this.detectJoins(prev, next);
    this.detectQuits(prev, next);
  }

  private detectBaseChanges(prev: Captured, next: Captured): void {
    for (const [id, b] of next.bases) {
      const before = prev.bases.get(id);
      if (!before) continue;
      if (before.owner === b.owner) continue;
      // Going neutral is silent — WinBolo only announces successful
      // captures, not destructions.
      if (b.owner === NEUTRAL) continue;
      // Allied migrations are silent. Check BOTH prev and next allies
      // bitmaps: a disconnect-migration clears the leaver's alliances on
      // the same tick the ownership flips, so prev.allies still has the
      // pact whereas next.allies doesn't.
      if (areAllied(prev.allies, before.owner, b.owner)) continue;
      if (areAllied(next.allies, before.owner, b.owner)) continue;
      const newName = nameOf(next.playerNames, b.owner);
      if (!newName) continue;
      if (before.owner === NEUTRAL) {
        this.sink.add(`${newName} captured a Neutral Base`);
      } else {
        const oldName = nameOf(prev.playerNames, before.owner) || nameOf(next.playerNames, before.owner);
        if (!oldName) continue;
        this.sink.add(`${newName} just stole base from ${oldName}`);
      }
    }
  }

  private detectPillChanges(prev: Captured, next: Captured): void {
    // Pill ownership transfers at pickup (movement.ts pickupPillIfDestroyed)
    // — matching WinBolo's tank.c:1780-1782. So the wire announcement fires
    // when prev.owner != next.owner on the same pill id, regardless of
    // carriedBy state. The carry-cycle place-back is silent because owner
    // doesn't change there (carrier already owns it from pickup).
    for (const [id, p] of next.pillboxes) {
      const before = prev.pillboxes.get(id);
      if (!before) continue;
      if (before.owner === p.owner) continue;
      // Going neutral is silent — destruction never announces on the wire.
      if (p.owner === NEUTRAL) continue;
      // Allied migrations / pickups across an active pact: silent. Check
      // BOTH prev and next allies bitmaps so disconnect-migrations (which
      // clear the leaver's alliances on the same tick) don't fire either.
      if (areAllied(prev.allies, before.owner, p.owner)) continue;
      if (areAllied(next.allies, before.owner, p.owner)) continue;
      const newName = nameOf(next.playerNames, p.owner);
      if (!newName) continue;
      if (before.owner === NEUTRAL) {
        this.sink.add(`${newName} captured a Neutral Pillbox`);
      } else {
        const oldName = nameOf(prev.playerNames, before.owner) || nameOf(next.playerNames, before.owner);
        if (!oldName) continue;
        this.sink.add(`${newName} just stole pillbox from ${oldName}`);
      }
    }
  }

  private detectLgmDeaths(prev: Captured, next: Captured): void {
    // The LGM enters Parachuting only after dying (lgm.ts: killLGM sets
    // status to Dead, the next tick promotes Dead → Parachuting). So a
    // transition from !Parachuting → Parachuting marks one death event.
    for (const [id, t] of next.tanks) {
      const before = prev.tanks.get(id);
      if (!before) continue;
      if (before.lgmStatus === LGM_PARACHUTING) continue;
      if (t.lgmStatus !== LGM_PARACHUTING) continue;
      const name = nameOf(next.playerNames, t.owner);
      if (!name) continue;
      this.sink.add(`${name} just lost his builder`);
    }
  }

  private detectAllianceChanges(prev: Captured, next: Captured): void {
    // The mutual-pact `allies[i]` bitmap is symmetric — bit j set in
    // allies[i] implies bit i set in allies[j]. We canonicalise by only
    // iterating pairs (i, j) where i < j, so each transition fires once.
    const now = this.now();
    const len = Math.min(prev.allies.length, next.allies.length);
    for (let i = 0; i < len; i++) {
      const prevBits = prev.allies[i] ?? 0;
      const nextBits = next.allies[i] ?? 0;
      // If neither prev nor next has any bits set for slot i, no pairs
      // involving i can have changed.
      if (prevBits === nextBits) continue;
      for (let j = i + 1; j < len; j++) {
        const mask = 1 << j;
        const wasAllied = (prevBits & mask) !== 0;
        const isAllied = (nextBits & mask) !== 0;
        if (wasAllied === isAllied) continue;

        // Disconnect-side-effect: if either side's name just emptied in
        // this snapshot, the alliance break is a consequence of the quit
        // (clearAlliesFor runs on leave). The dedicated "<Name> has quit
        // game" line already covers that case — suppress the alliance
        // line to avoid double-announcing.
        if (!isAllied) {
          const aGone = (prev.playerNames[i] ?? '') !== '' && (next.playerNames[i] ?? '') === '';
          const bGone = (prev.playerNames[j] ?? '') !== '' && (next.playerNames[j] ?? '') === '';
          if (aGone || bGone) continue;
        }

        const key = `${i}:${j}`;
        const lastTs = this.alliancePairLastFired.get(key) ?? -Infinity;
        // Always update the cooldown clock — suppressed transitions count,
        // so a sustained spam loop never escapes the cooldown window.
        this.alliancePairLastFired.set(key, now);
        if (now - lastTs < ALLIANCE_COOLDOWN_MS) continue;

        const aName = nameOf(next.playerNames, i) || nameOf(prev.playerNames, i);
        const bName = nameOf(next.playerNames, j) || nameOf(prev.playerNames, j);
        if (!aName || !bName) continue;
        if (isAllied) {
          this.sink.add(`${aName} and ${bName} just formed an alliance`);
        } else {
          this.sink.add(`${aName} and ${bName} just broke their alliance`);
        }
      }
    }
  }

  private detectDrownings(prev: Captured, next: Captured): void {
    // A drowning emits a `tankSinking` sound at the death tile in the same
    // tick as the alive→dead transition (damage.ts drownTank). To pin a
    // sound event to a specific tank we compare its world coords to the
    // pre-death position of any tank that just transitioned alive→false.
    // Coords match exactly because the sound and killTank both read tank.x
    // / tank.y in the same tick.
    if (next.sunkAt.length === 0) return;
    for (const [id, t] of next.tanks) {
      const before = prev.tanks.get(id);
      if (!before || !before.alive || t.alive) continue;
      const match = next.sunkAt.find((s) => s.x === before.x && s.y === before.y);
      if (!match) continue;
      const name = nameOf(next.playerNames, t.owner) || nameOf(prev.playerNames, t.owner);
      if (!name) continue;
      this.sink.add(`${name} just drowned in deep sea`);
    }
  }

  private detectJoins(prev: Captured, next: Captured): void {
    // Inverse of detectQuits: a slot transitioning from empty to a real
    // name is a new player joining the field.
    const len = Math.max(prev.playerNames.length, next.playerNames.length);
    for (let slot = 0; slot < len; slot++) {
      const before = prev.playerNames[slot] ?? '';
      const after = next.playerNames[slot] ?? '';
      if (before === '' && after !== '') {
        this.sink.add(`"${after}" has joined the game`);
      }
    }
  }

  private detectQuits(prev: Captured, next: Captured): void {
    // Player slots are indexed 0..MAX_PLAYERS-1. A slot transitioning from
    // a non-empty name to empty means that player disconnected and the
    // slot was reclaimed.
    const len = Math.max(prev.playerNames.length, next.playerNames.length);
    for (let slot = 0; slot < len; slot++) {
      const before = prev.playerNames[slot] ?? '';
      const after = next.playerNames[slot] ?? '';
      if (before !== '' && after === '') {
        this.sink.add(`"${before}" has quit game`);
      }
    }
  }
}

function capture(snap: SnapshotForDiff): Captured {
  const bases = new Map<number, BaseLite>();
  for (const b of snap.bases) bases.set(b.id, { id: b.id, owner: b.owner });
  const pillboxes = new Map<number, PillLite>();
  for (const p of snap.pillboxes) pillboxes.set(p.id, { id: p.id, owner: p.owner, carriedBy: p.carriedBy });
  const tanks = new Map<number, TankLite>();
  for (const t of snap.tanks)
    tanks.set(t.id, { id: t.id, owner: t.owner, alive: t.alive, x: t.x, y: t.y, lgmStatus: t.lgmStatus });
  const sunkAt: Array<{ x: number; y: number }> = [];
  for (const e of snap.soundEvents) if (e.kind === 'tankSinking') sunkAt.push({ x: e.x, y: e.y });
  return {
    bases,
    pillboxes,
    tanks,
    playerNames: snap.playerNames.slice(),
    allies: snap.allies.slice(),
    sunkAt,
  };
}

function nameOf(names: ReadonlyArray<string>, slot: number): string {
  if (slot < 0 || slot === NEUTRAL) return '';
  return names[slot] ?? '';
}

function areAllied(allies: ReadonlyArray<number>, a: number, b: number): boolean {
  if (a < 0 || b < 0) return false;
  if (a === NEUTRAL || b === NEUTRAL) return false;
  if (a === b) return true;
  return ((allies[a] ?? 0) & (1 << b)) !== 0;
}
